/**
 * FileViewerTab — renders an asset file inline as body-content (not a modal).
 * Supports PDF native viewing and automatic conversion of Office/image formats to PDF.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  PdfViewerComponent,
  Inject,
  Toolbar,
  Magnification,
  Navigation,
  LinkAnnotation,
  BookmarkView,
  ThumbnailView,
  Print,
  TextSelection,
  TextSearch,
  Annotation,
  FormDesigner,
  FormFields,
} from '@syncfusion/ej2-react-pdfviewer';
import { ImageEditorComponent } from '@syncfusion/ej2-react-image-editor';
import { SpreadsheetComponent } from '@syncfusion/ej2-react-spreadsheet';
import * as XLSX from 'xlsx';
import { ErrorBoundary } from '../../components/ErrorBoundary/ErrorBoundary';
import type { AssetItem } from './assetmodel';
import {
  convertFileToPdf,
  CONVERT_TO_PDF_EXTS,
  getWorkspaceAssetContent,
  getWorkspaceAssetId,
} from '../../services/workspaceAssetsService';
import '../../styles/file-viewer-tab.css';

const SF_PDF_RESOURCE_URL = 'https://cdn.syncfusion.com/ej2/34.1.33/dist/ej2-pdfviewer-lib';
const SPREADSHEET_EXTENSIONS = new Set(['xls', 'xlsx', 'xlsm', 'csv']);
const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'bmp', 'tiff']);

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result);
      else reject(new Error('Unable to read image for preview.'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('Unable to read image for preview.'));
    reader.readAsDataURL(blob);
  });
}

function resolveExt(asset: AssetItem): string {
  const raw = asset.FileExtension || asset.FileName?.split('.').pop() || '';
  return raw.toLowerCase().replace(/^\./, '');
}

function toDomIdSegment(value: string): string {
  return value.trim().replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'asset';
}

export interface FileViewerTabProps {
  asset: AssetItem;
}

export const FileViewerTab = React.memo(function FileViewerTab({ asset }: FileViewerTabProps) {
  const assetId = getWorkspaceAssetId(asset);
  const ext = resolveExt(asset);
  const isPdf = ext === 'pdf';
  const isSpreadsheet = SPREADSHEET_EXTENSIONS.has(ext);
  const isImage = IMAGE_EXTENSIONS.has(ext);
  const needsConversion = !isPdf && !isSpreadsheet && !isImage && CONVERT_TO_PDF_EXTS.has(ext);
  const isPreviewSupported = isPdf || isSpreadsheet || isImage || needsConversion;

  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const [isImageLoading, setIsImageLoading] = useState(isImage);
  const [imageError, setImageError] = useState<string | null>(null);
  const [spreadsheetSheets, setSpreadsheetSheets] = useState<Array<{
    name: string;
    ranges: Array<{ dataSource: Record<string, unknown>[] }>;
  }>>([]);
  const [isSpreadsheetLoading, setIsSpreadsheetLoading] = useState(isSpreadsheet);
  const [spreadsheetError, setSpreadsheetError] = useState<string | null>(null);
  const [isConverting, setIsConverting] = useState(needsConversion);
  // only show PDF loading state when we'll actually render the viewer
  const [isPdfLoading, setIsPdfLoading] = useState(isPdf || needsConversion);
  const [convertError, setConvertError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const imageEditorRef = useRef<ImageEditorComponent | null>(null);

  // Content load/conversion effect - runs when the selected asset changes
  useEffect(() => {
    let cancelled = false;
    const abortController = new AbortController();
    setImageDataUrl(null);
    setIsImageLoading(isImage);
    setImageError(null);
    setSpreadsheetSheets([]);
    setIsSpreadsheetLoading(isSpreadsheet);
    setSpreadsheetError(null);
    setIsConverting(needsConversion);
    setIsPdfLoading(isPdf || needsConversion);
    setConvertError(null);
    setDownloadError(null);
    setDocumentUrl(null);

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    if (!isPdf && !isSpreadsheet && !isImage && !needsConversion) {
      setIsConverting(false);
      setIsPdfLoading(false);
      setIsImageLoading(false);
      setIsSpreadsheetLoading(false);
      return () => { cancelled = true; };
    }

    (async () => {
      try {
        const sourceBlob = await getWorkspaceAssetContent(asset, abortController.signal);

        if (isSpreadsheet) {
          const workbook = ext === 'csv'
            ? XLSX.read(await sourceBlob.text(), { type: 'string' })
            : XLSX.read(await sourceBlob.arrayBuffer(), { type: 'array' });
          const sheets = workbook.SheetNames.map((name) => ({
            name,
            ranges: [{
              dataSource: XLSX.utils.sheet_to_json<Record<string, unknown>>(workbook.Sheets[name], { defval: '' }),
            }],
          }));

          if (!cancelled) setSpreadsheetSheets(sheets);
          return;
        }

        if (isImage) {
          const dataUrl = await blobToDataUrl(sourceBlob);
          if (!cancelled) {
            setImageDataUrl(dataUrl);
            setIsImageLoading(false);
          }
          return;
        }

        const pdfBlob = needsConversion
          ? await convertFileToPdf(new File([sourceBlob], asset.FileName, { type: sourceBlob.type || 'application/octet-stream' }), abortController.signal)
          : sourceBlob;

        if (!cancelled) {
          const url = URL.createObjectURL(pdfBlob);
          blobUrlRef.current = url;
          setDocumentUrl(url);
          setIsPdfLoading(true);
        }
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : 'Failed to load file for preview.';
        if (!cancelled) {
          if (isSpreadsheet) setSpreadsheetError(message);
          else if (isImage) setImageError(message);
          else setConvertError(message);
        }
      } finally {
        if (!cancelled) {
          setIsConverting(false);
          setIsImageLoading(false);
          setIsSpreadsheetLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [asset, assetId, ext, isSpreadsheet, isImage, isPdf, needsConversion]);

  const openImage = useCallback(() => {
    if (!imageEditorRef.current || !imageDataUrl) return;

    imageEditorRef.current.open(imageDataUrl);
  }, [imageDataUrl]);

  useEffect(() => {
    openImage();
  }, [openImage]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  const documentPath = documentUrl;
  const showPdfViewer = !!documentUrl;
  const showImageViewer = isImage && !!imageDataUrl && !imageError;
  const pdfViewerId = `fvt-pdf-${toDomIdSegment(assetId)}`;
  const imageViewerId = `fvt-image-${toDomIdSegment(assetId)}`;

  const handleDocumentLoad = useCallback(() => {
    setTimeout(() => setIsPdfLoading(false), 100);
  }, []);

  const handleDownload = useCallback(async () => {
    const fileName = asset.FileName || asset.Name || 'download';
    setDownloadError(null);
    try {
      const blob = await getWorkspaceAssetContent(asset);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setDownloadError('The file could not be downloaded.');
    }
  }, [asset]);

  return (
    <div className="fvt-root">
      {/* ── Body ──────────────────────────────────────────────── */}
      <div className="fvt-body">
        {isPreviewSupported && (isConverting || isPdfLoading) && !convertError && (
          <div className="fvt-converting">
            <div className="fvt-spinner" role="status" aria-label={isConverting ? 'Converting to PDF' : 'Loading PDF'} />          </div>
        )}

        {isImage && isImageLoading && !imageError && (
          <div className="fvt-converting">
            <div className="fvt-spinner" role="status" aria-label="Loading image" />
            <p className="fvt-converting__msg">Loading image…</p>
          </div>
        )}

        {isImage && imageError && !isImageLoading && (
          <div className="fvt-unavailable">
            <p className="fvt-unavailable__title">Image preview failed</p>
            <p className="fvt-unavailable__sub">The image could not be loaded. You can still download the original file.</p>
            <button type="button" className="fvt-unavailable__download-btn" onClick={handleDownload}>
              <span className="material-symbols-outlined">download</span>
              Download {asset.FileName || asset.Name}
            </button>
            {downloadError && <p className="fvt-unavailable__error" aria-live="polite">{downloadError}</p>}
          </div>
        )}

        {showImageViewer && (
          <div className="fvt-image-editor-wrap">
            <ErrorBoundary
              level="component"
              componentName="FileViewerSyncfusionImageEditor"
              fallback={(
                <div className="fvt-image-wrap">
                  <img className="fvt-image" src={imageDataUrl} alt={asset.FileName || asset.Name || 'Image preview'} />
                </div>
              )}
            >
              <ImageEditorComponent
                key={assetId}
                id={imageViewerId}
                ref={(instance: ImageEditorComponent | null) => { imageEditorRef.current = instance; }}
                toolbar={[]}
                created={openImage}
                style={{ height: '100%', width: '100%' }}
              />
            </ErrorBoundary>
          </div>
        )}

        {convertError && !isConverting && (
          <div className="fvt-unavailable">
            <div className="fvt-unavailable__icon-wrap">
              <span className="material-symbols-outlined fvt-unavailable__icon">error_outline</span>
            </div>
            <p className="fvt-unavailable__title">Preview failed</p>
            <p className="fvt-unavailable__sub">The file could not be converted for preview. You can still download the original file.</p>
            <button type="button" className="fvt-unavailable__download-btn" onClick={handleDownload}>
              <span className="material-symbols-outlined">download</span>
              Download {asset.FileName || asset.Name}
            </button>
            {downloadError && <p className="fvt-unavailable__error" aria-live="polite">{downloadError}</p>}
          </div>
        )}

        {showPdfViewer && documentPath && (
          <div className="fvt-pdf-wrap">
            <ErrorBoundary
              level="component"
              componentName="FileViewerSyncfusionPdf"
              fallback={(
                <iframe
                  title={asset.FileName || asset.Name || 'PDF preview'}
                  src={documentPath}
                  style={{ height: '100%', width: '100%', border: 'none' }}
                />
              )}
            >
              <PdfViewerComponent
                key={assetId}
                id={pdfViewerId}
                documentPath={documentPath}
                documentLoad={handleDocumentLoad}
                resourceUrl={SF_PDF_RESOURCE_URL}
                enableAnnotation
                enableFreeText
                enableTextSelection
                enableTextSearch
                enablePrint
                enableMagnification
                enableNavigation
                enableThumbnail
                enableBookmark
                style={{ height: '100%', width: '100%' }}
              >
                <Inject
                  services={[
                    Toolbar, Magnification, Navigation, LinkAnnotation,
                    BookmarkView, ThumbnailView, Print, TextSelection,
                    TextSearch, Annotation, FormDesigner, FormFields,
                  ]}
                />
              </PdfViewerComponent>
            </ErrorBoundary>
          </div>
        )}

        {isSpreadsheet && isSpreadsheetLoading && !spreadsheetError && (
          <div className="fvt-converting">
            <div className="fvt-spinner" role="status" aria-label="Loading spreadsheet" />
            <p className="fvt-converting__msg">Loading spreadsheet…</p>
          </div>
        )}

        {isSpreadsheet && spreadsheetError && !isSpreadsheetLoading && (
          <div className="fvt-unavailable">
            <p className="fvt-unavailable__title">Spreadsheet preview failed</p>
            <p className="fvt-unavailable__sub">The spreadsheet could not be loaded. You can still download the original file.</p>
            <button type="button" className="fvt-unavailable__download-btn" onClick={handleDownload}>
              <span className="material-symbols-outlined">download</span>
              Download {asset.FileName || asset.Name}
            </button>
            {downloadError && <p className="fvt-unavailable__error" aria-live="polite">{downloadError}</p>}
          </div>
        )}

        {isSpreadsheet && !isSpreadsheetLoading && !spreadsheetError && spreadsheetSheets.length > 0 && (
          <div className="fvt-spreadsheet-wrap">
            <SpreadsheetComponent
              key={assetId}
              sheets={spreadsheetSheets}
              allowEditing={false}
              showRibbon={false}
              style={{ height: '100%', width: '100%' }}
            />
          </div>
        )}

        {/* Unsupported file type */}
        {!isPreviewSupported && (
          <div className="fvt-unavailable">
            <p className="fvt-unavailable__title">Preview is not available for this file type.</p>
          </div>
        )}
      </div>
    </div>
  );
});

export default FileViewerTab;
